import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

const resetPasswordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters long"),
  confirmPassword: z.string().min(8, "Password must be at least 8 characters long"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    // Clean up the fromEmail parameter from URL when component mounts
    const fromEmail = searchParams.get('fromEmail');
    if (fromEmail === 'true') {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('fromEmail');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams]);

  const onSubmit = async (data: ResetPasswordFormValues) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Update the user's password - Supabase already handled token verification
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password
      });

      if (updateError) {
        console.error("Password update error:", updateError);
        setError(updateError.message);
      } else {
        setIsSuccess(true);
        
        toast({
          title: "Password updated successfully!",
          description: "Your password has been successfully updated, please log in",
        });
        
        // Sign out the user so they need to log in with new password
        await supabase.auth.signOut();
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row">
        <div className="hidden md:flex md:w-1/2 bg-emu-blue items-center justify-center p-0">
          <div className="w-full h-full flex items-center justify-center">
            <img 
              src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
              alt="EMU Logo" 
              className="w-full h-full object-cover" 
            />
          </div>
        </div>
        
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 md:p-10">
          <div className="w-full max-w-md text-center">
            <div className="flex md:hidden justify-center mb-8">
              <img 
                src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
                alt="EMU Logo" 
                className="h-14" 
              />
            </div>
            <h1 className="text-2xl font-bold text-emu-blue mb-4">Password Updated!</h1>
            <p className="text-gray-600 mb-6">
              Your password has been successfully updated, please log in with your new password.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-emu-blue hover:bg-emu-darkblue text-white"
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 bg-emu-blue items-center justify-center p-0">
        <div className="w-full h-full flex items-center justify-center">
          <img 
            src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
            alt="EMU Logo" 
            className="w-full h-full object-cover" 
          />
        </div>
      </div>
      
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 md:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex md:hidden justify-center mb-8">
              <img 
                src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
                alt="EMU Logo" 
                className="h-14" 
              />
            </div>
            <h1 className="text-2xl font-bold text-emu-blue mb-2">Reset Your Password</h1>
            <p className="text-sm text-gray-600 mb-4">
              Please enter your new password below.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Enter your new password" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Confirm your new password" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full bg-emu-blue hover:bg-emu-darkblue text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-emu-blue hover:underline"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;